import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import AuthCard from './AuthCard';
import styles from './auth.module.css';

const MIN_PASSWORD_LENGTH = 8;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { t } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t('auth.passwordTooShort', { min: MIN_PASSWORD_LENGTH }));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || t('auth.resetPassword.invalidLink'));
    }

    setLoading(false);
  };

  if (!token) {
    return (
      <Container component="main" maxWidth="xs">
        <Box className={styles.wrap}>
          <AuthCard>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              {t('auth.resetPassword.title')}
            </Typography>
            <Alert severity="error" className={styles.alert}>
              {t('auth.resetPassword.missingToken')}
            </Alert>
            <Box className={`${styles.linkRowCenter} ${styles.mt3}`}>
              <Link to="/forgot-password" className={styles.link}>
                <Typography variant="body2" color="primary">
                  {t('auth.resetPassword.requestNewLink')}
                </Typography>
              </Link>
            </Box>
          </AuthCard>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box className={styles.wrap}>
        <AuthCard>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {t('auth.resetPassword.title')}
          </Typography>

          {success ? (
            <>
              <Alert severity="success" className={styles.alert}>
                {t('auth.resetPassword.success')}
              </Alert>
              <Box className={`${styles.linkRowCenter} ${styles.mt3}`}>
                <Link to="/login" className={styles.link}>
                  <Typography variant="body2" color="primary">
                    {t('auth.resetPassword.signInWithNew')}
                  </Typography>
                </Link>
              </Box>
            </>
          ) : (
            <>
              {error && (
                <Alert severity="error" className={styles.alert}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} className={styles.formBox}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="newPassword"
                  label={t('auth.newPassword')}
                  type="password"
                  id="newPassword"
                  autoComplete="new-password"
                  autoFocus
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  error={newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH}
                  helperText={newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH ? t('auth.passwordTooShort', { min: MIN_PASSWORD_LENGTH }) : ''}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label={t('auth.confirmNewPassword')}
                  type="password"
                  id="confirmPassword"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                  helperText={
                    confirmPassword.length > 0 && newPassword !== confirmPassword
                      ? t('auth.passwordsDoNotMatch')
                      : ''
                  }
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  className={styles.submitBtnLg}
                  disabled={loading}
                >
                  {loading ? t('auth.resetPassword.submitting') : t('auth.resetPassword.submit')}
                </Button>
                <Box className={`${styles.linkRowCenter} ${styles.mt1}`}>
                  <Link to="/login" className={styles.link}>
                    <Typography variant="body2" color="primary">
                      {t('auth.backToSignIn')}
                    </Typography>
                  </Link>
                </Box>
              </Box>
            </>
          )}
        </AuthCard>
      </Box>
    </Container>
  );
};

export default ResetPassword;
